import { NgClass, NgFor, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { MatRadioModule } from '@angular/material/radio';
import { TranslateModule } from '@ngx-translate/core';
import { Glass, GlassQuestion } from '@sc/data-access-models';
import { CarMiniatureImgComponent, IconComponent } from '../../components';
import { ForceRadioNameDirective } from '../../directives';
import { LabelRequiredPipe } from '../../pipes';

@Component({
  selector: 'sc-glass-choice-question',
  styleUrls: ['./glass-choice-question.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    NgFor,
    NgClass,
    CarMiniatureImgComponent,
    IconComponent,
    MatRadioModule,
    ForceRadioNameDirective,
    LabelRequiredPipe,
    TranslateModule,
  ],
  template: `
    <fieldset [id]="'questionGlass-' + glassQuestion?.key">
      <legend
        [id]="questionLegendId"
        class="_questions_label sc-design-text-3-bold"
        tabindex="-1"
      >
        {{ getQuestionText() | labelRequired: true }}
      </legend>

      <mat-radio-group preventDefaultArrow [name]="'questionName-' + index">
        <ul>
          <li *ngFor="let answerItem of getPossibleAnswers()">
            <mat-radio-button
              class="radio-button-block"
              [value]="answerItem"
              [id]="$any(answerItem)"
              [forceRadioName]="$any(answerItem)"
              [checked]="answer === answerItem"
              [ngClass]="{
                'radio-button-container-selected': answer === answerItem
              }"
              [attr.data-testid]="answerItem"
              (click)="onAnswerChange(answerItem)"
              (keydown.enter)="onAnswerChange(answerItem)"
              (keydown.space)="onAnswerChange(answerItem)"
            >
              <span class="sc-design-text-3">{{
                getAnswerText(answerItem) | translate
              }}</span>
              <sc-car-miniature-img
                *ngIf="index === 0"
                aria-hidden="true"
                [glassExtended]="$any(answerItem)"
              ></sc-car-miniature-img>

              <sc-icon class="selected-icon" name="check_bis"></sc-icon>
            </mat-radio-button>
          </li>
        </ul>
      </mat-radio-group>
    </fieldset>
  `,
})
export class GlassChoiceQuestionComponent {
  @Input() index: number;
  @Input() glass: Glass;
  @Input() answer: string | boolean;
  @Input() glassQuestion: GlassQuestion;
  @Input() questionLegendId: string;
  @Output() answerChangeEvent = new EventEmitter<string | boolean>();

  getAnswerItemString(answerItem: string | boolean): string {
    return answerItem as string;
  }

  getAnswerItemGlass(answerItem: string | boolean): Glass {
    return answerItem as Glass;
  }

  getQuestionAddress(): string {
    return `CustomerDeclaration.GlassChoicePage.${
      this.index === 0 ? '' : this.glass + '.'
    }${this.glassQuestion?.key}`;
  }

  getQuestionText(): string {
    return `${this.getQuestionAddress()}.questionText`;
  }

  getAnswerText(answerItem: string | boolean) {
    return answerItem !== null && answerItem !== undefined
      ? `${this.getQuestionAddress()}.answers.${answerItem}`
      : '';
  }

  getPossibleAnswers(): (string | boolean)[] {
    return this.glassQuestion?.values?.map((value) =>
      typeof value === 'object' ? value.value : value
    );
  }

  onAnswerChange(answer: string | boolean) {
    this.answerChangeEvent.emit(answer);
  }
}
